import type { CSSProperties } from "react";

const TOOLTIP_GAP = 12;
const TOOLTIP_Z_INDEX = 1000000;
const TOOLTIP_MAX_WIDTH = 300;
const MIN_SPACE_VERTICAL = 120;
const MIN_SPACE_HORIZONTAL = 250;

type Placement = "top" | "bottom" | "left" | "right";

interface TourTooltipProps {
  targetRect: DOMRect | null;
  text: string;
  stepNumber: number;
  totalSteps: number;
}

const calculatePlacement = (rect: DOMRect): Placement => {
  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;
  const spaceRight = window.innerWidth - rect.right;
  const spaceLeft = rect.left;

  if (spaceBelow >= MIN_SPACE_VERTICAL) return "bottom";
  if (spaceAbove >= MIN_SPACE_VERTICAL) return "top";
  if (spaceRight >= MIN_SPACE_HORIZONTAL) return "right";
  if (spaceLeft >= MIN_SPACE_HORIZONTAL) return "left";
  return "bottom";
};

const getPositionStyle = (rect: DOMRect, placement: Placement): CSSProperties => {
  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;
  const halfMax = TOOLTIP_MAX_WIDTH / 2;
  const clampedX = Math.max(halfMax + 8, Math.min(window.innerWidth - halfMax - 8, centerX));

  switch (placement) {
    case "bottom":
      return { top: rect.bottom + TOOLTIP_GAP, left: clampedX, transform: "translateX(-50%)" };
    case "top":
      return {
        bottom: window.innerHeight - rect.top + TOOLTIP_GAP,
        left: clampedX,
        transform: "translateX(-50%)",
      };
    case "right":
      return { top: centerY, left: rect.right + TOOLTIP_GAP, transform: "translateY(-50%)" };
    case "left":
      return {
        top: centerY,
        right: window.innerWidth - rect.left + TOOLTIP_GAP,
        transform: "translateY(-50%)",
      };
  }
};

const TourTooltip = ({ targetRect, text, stepNumber, totalSteps }: TourTooltipProps) => {
  if (!targetRect) return null;

  const placement = calculatePlacement(targetRect);
  const positionStyle = getPositionStyle(targetRect, placement);

  return (
    <div
      className="tour-custom-tooltip"
      style={{
        position: "fixed",
        zIndex: TOOLTIP_Z_INDEX,
        maxWidth: TOOLTIP_MAX_WIDTH,
        ...positionStyle,
      }}
    >
      <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-lg">
        <p className="mb-2 text-sm text-gray-700">{text}</p>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400">
            Step {stepNumber} of {totalSteps}
          </span>
          <span className="text-xs text-gray-400">Click to continue</span>
        </div>
      </div>
    </div>
  );
};

export default TourTooltip;
